/**
 * The zone naming convention that separates side-pass alerts from collision alerts.
 *
 * Two unrelated conventions used to collide on one string. The dashboard's attenuator layout editor
 * encodes the truck's position on the road into the protected zone's name — "Attenuator Zone:right"
 * means the truck sits in the right lane — while this helper is asked which *side* a vehicle passed
 * on. A bare ":right" suffix cannot answer both questions, and reading the lane suffix as a pass
 * side filed every right-lane truck's collision zone as a right-side pass: the collision trigger
 * disappeared from the console's device settings, the fleet matrix reported it "Missing", and the
 * cab labelled real collisions as passes. Right was the default lane, so this was the common case;
 * only center-lane trucks behaved correctly, because "center" happens not to match.
 *
 * Side-pass zones therefore carry their own prefix, and nothing else is allowed to match. A zone
 * that merely *ends* in ":left" or ":right" is not a side pass — it is the protected zone of a truck
 * parked in that lane, and it must read as a collision zone.
 *
 * The parsing lives here once because the cab's Home diagram and Alerts feed, the console's alert
 * defaults, and the event reports all have to make the same call from the same name; drifting copies
 * of this regex would mean an alert filed as a side pass on one screen and a collision on another.
 */

export type PassSide = "left" | "right";

/** Prefix that marks a zone as watching for a pass rather than defending the truck. */
export const SIDE_PASS_ZONE_PREFIX = "Side Pass";

/**
 * Anchored on purpose. An unanchored `:(left|right)$` is what caused the original conflation — it
 * matched any name that happened to end that way, including the attenuator layout editor's.
 */
const SIDE_PASS_ZONE_PATTERN = /^\s*side\s*pass\s*:\s*(left|right)\s*$/i;

/** The canonical name for a side-pass zone. Use this rather than composing the string by hand. */
export function sidePassZoneName(side: PassSide): string {
    return `${SIDE_PASS_ZONE_PREFIX}:${side}`;
}

/**
 * Which side a zone watches, or undefined when the zone is not a side-pass zone at all.
 *
 * Undefined is the answer for the protected/collision zone, so callers testing
 * `!sideOfZoneName(name)` to find the collision trigger keep working.
 */
export function sideOfZoneName(name: string | null | undefined): PassSide | undefined {
    const match = name?.match(SIDE_PASS_ZONE_PATTERN);
    if (!match) return undefined;
    return match[1].toLowerCase() === "left" ? "left" : "right";
}
